import React, { useState, useEffect, useContext, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import api from "../services/api";
import Message from "./Message";
import CommunityMap from "./CommunityMap";
import { FaSearch, FaHashtag, FaUser, FaChartLine, FaSpinner, FaUserPlus, FaProjectDiagram, FaList } from "react-icons/fa";
import { getProfilePicture } from "../utils/avatar";

// Auto-discover topic files for stance labels
const topicContext = require.context("../data/survey_topics", false, /^\.\/topic_.*\.json$/);
const TOPIC_STANCE_LABELS = {};
topicContext.keys().forEach((filename) => {
  const topic = topicContext(filename);
  if (topic.topicName && topic.stanceLabels) {
    TOPIC_STANCE_LABELS[topic.topicName] = topic.stanceLabels;
  }
});

const Search = () => {
  const { user } = useContext(AuthContext);
  const { condition, instanceId } = useContext(SurveyContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [activeTab, setActiveTab] = useState(searchParams.get("type") || "all");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trending, setTrending] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [followingUsers, setFollowingUsers] = useState([]);
  const [browseUsers, setBrowseUsers] = useState([]);
  const [conditionConfig, setConditionConfig] = useState(null);
  const [userView, setUserView] = useState("list"); // "list" or "map"

  // Debounced search function
  const performSearch = useCallback(async (searchQuery, searchType = "all") => {
    if (!searchQuery.trim()) {
      setResults(null);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get("/search", {
        params: {
          q: searchQuery,
          type: searchType,
          condition,
          limit: 20
        }
      });
      setResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
      setResults({ error: "Search failed. Please try again." });
    } finally {
      setLoading(false);
    }
  }, [condition]);

  // Get search suggestions
  const getSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await api.get("/search/suggestions", {
        params: {
          q: searchQuery,
          condition,
          limit: 5
        }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Suggestions error:", error);
      setSuggestions([]);
    }
  }, [condition]);

  // Load trending hashtags and condition config
  useEffect(() => {
    const loadTrending = async () => {
      try {
        const response = await api.get("/search/trending", {
          params: { condition, limit: 10, timeframe: "24h" }
        });
        setTrending(response.data.trending || []);
      } catch (error) {
        console.error("Trending error:", error);
      }
    };

    const loadConditionConfig = async () => {
      if (!condition) return;
      try {
        const response = await api.get(`/conditions/${condition}`);
        setConditionConfig(response.data);
      } catch (error) {
        console.error("Condition config error:", error);
      }
    };

    const loadFollowingAndUsers = async () => {
      try {
        const filterParams = instanceId ? { instanceId } : { condition };
        const response = await api.get("/users", {
          params: { ...filterParams, limit: 30 }
        });
        setBrowseUsers(response.data || []);

        if (user?.userId) {
          const followingResponse = await api.get(`/users/${user.userId}/following`, {
            params: filterParams
          });
          setFollowingUsers(followingResponse.data.map(u => u._id));
        }
      } catch (error) {
        console.error("Browse users error:", error);
      }
    };

    loadTrending();
    loadConditionConfig();
    loadFollowingAndUsers();
  }, [condition, instanceId, user]);

  // Handle URL changes
  useEffect(() => {
    const urlQuery = searchParams.get("q") || "";
    const urlType = searchParams.get("type") || "all";

    setQuery(urlQuery);
    setActiveTab(urlType);

    if (urlQuery) {
      performSearch(urlQuery, urlType);
    }
  }, [searchParams, performSearch]);

  // Handle search input changes
  useEffect(() => {
    const timer = setTimeout(() => {
      getSuggestions(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, getSuggestions]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setSearchParams({ q: query, type: activeTab });
    setShowSuggestions(false);
    performSearch(query, activeTab);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (query.trim()) {
      setSearchParams({ q: query, type: tab });
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.display);
    setShowSuggestions(false);
    setSearchParams({ q: suggestion.display, type: activeTab });
  };

  const handleHashtagClick = (hashtag) => {
    const hashtagQuery = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
    setQuery(hashtagQuery);
    setSearchParams({ q: hashtagQuery, type: "all" });
  };

  const handleInteraction = async (type, targetId) => {
    // Only call for types NOT already logged server-side.
    // Likes, comments, follows, unfollows are logged by their respective route handlers.
    const serverLoggedTypes = ["like", "unlike", "comment", "follow", "unfollow"];
    if (serverLoggedTypes.includes(type)) return;
    if (user && user._id) {
      try {
        await api.post(`/interactions/${user._id}`, {
          type,
          targetId,
          condition,
        });
      } catch (error) {
        console.error("Error recording interaction:", error);
      }
    }
  };

  const handleFollow = async (userId) => {
    try {
      if (!user || !user.userId) return;
      await api.post(`/follow`, { followeeId: userId, instanceId, condition });
      handleInteraction("follow", userId);
      setFollowingUsers(prev => [...prev, userId]);
    } catch (err) {
      console.error("Error following user:", err);
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      if (!user || !user.userId) return;
      await api.post(`/follow/unfollow`, { followeeId: userId, instanceId, condition });
      handleInteraction("unfollow", userId);
      setFollowingUsers(prev => prev.filter(id => id !== userId));
    } catch (err) {
      console.error("Error unfollowing user:", err);
    }
  };

  const topicName = conditionConfig?.topic?.name;
  const stanceLabels = TOPIC_STANCE_LABELS[topicName] || conditionConfig?.topic?.stanceLabels;

  const isSearching = query.trim().length > 0;
  const followingSet = new Set(followingUsers);

  return (
    <div className="flex min-h-screen bg-spiders-bg">
      {/* Main Content */}
      <div className="flex-1 max-w-3xl lg:border-r border-spiders-border">
        <div className="p-4 lg:p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold mb-4 text-spiders-text-primary">
              {isSearching ? "Search" : "Explore"}
            </h1>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="relative">
              <div className="flex">
                <div className="relative flex-1">
                  <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-spiders-text-muted" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setShowSuggestions(true);
                      if (!e.target.value.trim()) {
                        setResults(null);
                      }
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="w-full pl-12 pr-4 py-3 bg-spiders-bg-surface text-spiders-text-primary rounded-l-xl border border-spiders-border focus:border-spiders-primary-500 focus:ring-1 focus:ring-spiders-primary-500 focus:outline-none transition-all placeholder-spiders-text-muted"
                    placeholder="Search posts, users, #hashtags..."
                  />

                  {/* Search Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-spiders-bg-surface border border-spiders-border border-t-0 rounded-b-xl shadow-lg z-10 overflow-hidden">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="w-full px-4 py-3 text-left hover:bg-spiders-bg-elevated flex items-center transition-colors"
                        >
                          {suggestion.type === "hashtag" ? (
                            <FaHashtag className="mr-3 text-spiders-accent-400" />
                          ) : (
                            <FaUser className="mr-3 text-spiders-success" />
                          )}
                          <span className="text-spiders-text-primary">{suggestion.display}</span>
                          {suggestion.count > 0 && (
                            <span className="ml-auto text-spiders-text-muted text-sm">
                              {suggestion.count} {suggestion.type === "hashtag" ? "posts" : "followers"}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-spiders-primary-500 text-white rounded-r-xl hover:bg-spiders-primary-600 disabled:opacity-50 flex items-center transition-colors"
                >
                  {loading ? <FaSpinner className="animate-spin" /> : <FaSearch />}
                </button>
              </div>
            </form>

            {/* Search Type Tabs — always visible for filtering */}
            <div className="flex space-x-2 mt-4">
                {[
                  { key: "all", label: "All", icon: FaSearch },
                  { key: "messages", label: "Posts", icon: null },
                  { key: "users", label: "Users", icon: FaUser },
                  { key: "hashtags", label: "Hashtags", icon: FaHashtag }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`px-4 py-2 rounded-lg flex items-center space-x-2 text-sm font-medium transition-colors ${
                      activeTab === tab.key
                        ? "bg-spiders-primary-500 text-white"
                        : "bg-spiders-bg-surface text-spiders-text-secondary hover:bg-spiders-bg-elevated border border-spiders-border"
                    }`}
                  >
                    {tab.icon && <tab.icon className="w-4 h-4" />}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
          </div>

          {/* Search Results */}
          {loading && (
            <div className="text-center py-12">
              <FaSpinner className="animate-spin text-spiders-primary-400 text-3xl mx-auto mb-3" />
              <p className="text-spiders-text-muted">Searching...</p>
            </div>
          )}

          {results && results.error && (
            <div className="text-center py-12">
              <p className="text-spiders-error">{results.error}</p>
            </div>
          )}

          {/* Default state: filtered by active tab */}
          {!loading && !isSearching && (
            <div className="space-y-8">
              {/* Community Members — shown on "all" or "users" tab */}
              {(activeTab === "all" || activeTab === "users") && <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-spiders-text-primary flex items-center gap-2">
                    <FaUser className="w-4 h-4 text-spiders-primary-400" />
                    Community Members
                  </h2>
                  <div className="flex rounded-lg border border-spiders-border overflow-hidden">
                    <button
                      onClick={() => setUserView("list")}
                      className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${
                        userView === "list"
                          ? "bg-spiders-primary-500 text-white"
                          : "bg-spiders-bg-surface text-spiders-text-muted hover:text-spiders-text-secondary"
                      }`}
                    >
                      <FaList className="w-3 h-3" />
                      List
                    </button>
                    <button
                      onClick={() => setUserView("map")}
                      className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors border-l border-spiders-border ${
                        userView === "map"
                          ? "bg-spiders-primary-500 text-white"
                          : "bg-spiders-bg-surface text-spiders-text-muted hover:text-spiders-text-secondary"
                      }`}
                    >
                      <FaProjectDiagram className="w-3 h-3" />
                      Map
                    </button>
                  </div>
                </div>

                {userView === "map" ? (
                  <CommunityMap
                    followedUsers={followingUsers}
                    onFollowChange={setFollowingUsers}
                    stanceLabels={stanceLabels}
                    proLabel={stanceLabels?.pro || "Support"}
                    contraLabel={stanceLabels?.contra || "Oppose"}
                    compact
                    context="app"
                  />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {browseUsers
                      .filter(u => u._id !== user?.userId)
                      .map((browseUser) => (
                        <div
                          key={browseUser._id}
                          className="spiders-card p-4 hover:bg-spiders-bg-elevated/50 transition-colors rounded-xl"
                        >
                          <div className="flex items-start gap-3">
                            <img
                              src={getProfilePicture(browseUser.username, browseUser.avatarSeed)}
                              alt={browseUser.username}
                              className="w-12 h-12 rounded-full flex-shrink-0 border-2 border-spiders-border cursor-pointer"
                              onClick={() => navigate(`/profile/${browseUser.username}`)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <button
                                  onClick={() => navigate(`/profile/${browseUser.username}`)}
                                  className="text-spiders-text-primary font-semibold text-sm hover:underline"
                                >
                                  @{browseUser.username}
                                </button>
                                {followingSet.has(browseUser._id) ? (
                                  <button
                                    onClick={() => handleUnfollow(browseUser._id)}
                                    className="px-2.5 py-1 bg-transparent border border-spiders-border text-spiders-text-secondary hover:border-spiders-error hover:text-spiders-error rounded-full transition-colors text-xs font-medium flex-shrink-0"
                                  >
                                    Following
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleFollow(browseUser._id)}
                                    className="spiders-btn-primary text-xs py-1 px-2.5 flex items-center gap-1 rounded-full flex-shrink-0"
                                  >
                                    <FaUserPlus className="w-3 h-3" />
                                    Follow
                                  </button>
                                )}
                              </div>
                              {browseUser.bio && (
                                <p className="text-spiders-text-muted text-xs line-clamp-2 mt-1 leading-relaxed">
                                  {browseUser.bio}
                                </p>
                              )}
                              <div className="flex items-center gap-3 mt-2 text-xs text-spiders-text-muted">
                                <span>{browseUser.followers?.length || 0} followers</span>
                                <span>{browseUser.following?.length || 0} following</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>}

              {/* Trending Hashtags — shown on "all" or "hashtags" tab */}
              {(activeTab === "all" || activeTab === "hashtags") && trending.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-spiders-text-primary mb-3 flex items-center gap-2">
                    <FaChartLine className="w-4 h-4 text-spiders-accent-400" />
                    Trending
                  </h2>
                  <div className="space-y-1">
                    {trending.slice(0, 8).map((hashtag, index) => (
                      <button
                        key={hashtag._id}
                        onClick={() => handleHashtagClick(hashtag.name || hashtag._id)}
                        className="w-full text-left p-3 rounded-lg hover:bg-spiders-bg-surface/80 transition-colors flex items-center gap-4 group"
                      >
                        <span className="text-spiders-text-muted text-xs font-medium w-5 text-right">{index + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-spiders-text-primary font-semibold text-sm group-hover:text-spiders-accent-400 transition-colors">
                            #{hashtag.name || hashtag._id}
                          </p>
                          <p className="text-spiders-text-muted text-xs">{hashtag.usageCount} {hashtag.usageCount === 1 ? "post" : "posts"}</p>
                        </div>
                        <FaHashtag className="w-3.5 h-3.5 text-spiders-accent-400/30 group-hover:text-spiders-accent-400/60 transition-colors" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {results && !results.error && (
            <div className="space-y-6">
              {/* Results Summary */}
              <div className="text-spiders-text-muted text-sm">
                Found {results.totalResults} results for &ldquo;{results.query}&rdquo;
              </div>

              {/* Messages */}
              {results.messages && results.messages.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-spiders-text-primary mb-4">Posts</h2>
                  <div className="border border-spiders-border rounded-xl overflow-hidden">
                    {results.messages.map((message) => (
                      <Message
                        key={message._id}
                        message={message}
                        onInteraction={handleInteraction}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Users */}
              {results.users && results.users.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-spiders-text-primary mb-4">Users</h2>
                  <div className="space-y-2">
                    {results.users.map((resultUser) => (
                      <div
                        key={resultUser._id}
                        className="spiders-card p-3 hover:bg-spiders-bg-elevated/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={getProfilePicture(resultUser.username, resultUser.avatarSeed)}
                            alt={resultUser.username}
                            className="w-10 h-10 rounded-full flex-shrink-0 border-2 border-spiders-border cursor-pointer"
                            onClick={() => navigate(`/profile/${resultUser.username}`)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => navigate(`/profile/${resultUser.username}`)}
                                className="text-spiders-text-primary font-semibold text-sm hover:underline"
                              >
                                @{resultUser.username}
                              </button>
                              <span className="text-spiders-text-muted text-xs">
                                {resultUser.followers?.length || 0} followers
                              </span>
                            </div>
                            {resultUser.bio && (
                              <p className="text-spiders-text-muted text-xs line-clamp-1 mt-0.5">
                                {resultUser.bio}
                              </p>
                            )}
                          </div>
                          {followingSet.has(resultUser._id) ? (
                            <button
                              onClick={() => handleUnfollow(resultUser._id)}
                              className="px-3 py-1.5 bg-transparent border border-spiders-border text-spiders-text-secondary hover:border-spiders-error hover:text-spiders-error rounded-full transition-colors text-xs font-medium flex-shrink-0"
                            >
                              Following
                            </button>
                          ) : (
                            <button
                              onClick={() => handleFollow(resultUser._id)}
                              className="spiders-btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 rounded-full flex-shrink-0"
                            >
                              <FaUserPlus className="w-3 h-3" />
                              Follow
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hashtags */}
              {results.hashtags && results.hashtags.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-spiders-text-primary mb-4">Hashtags</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {results.hashtags.map((hashtag) => (
                      <button
                        key={hashtag._id}
                        onClick={() => handleHashtagClick(hashtag.name || hashtag._id)}
                        className="spiders-card p-4 text-left hover:bg-spiders-bg-elevated/50 transition-colors"
                      >
                        <div className="flex items-center">
                          <FaHashtag className="text-spiders-accent-400 mr-3 w-5 h-5" />
                          <div className="flex-1">
                            <h3 className="text-spiders-text-primary font-bold">#{hashtag.name || hashtag._id}</h3>
                            <p className="text-spiders-text-muted text-sm">
                              {hashtag.usageCount} posts
                            </p>
                            {hashtag.trendingScore > hashtag.usageCount && (
                              <div className="flex items-center mt-1">
                                <FaChartLine className="text-spiders-success mr-1 text-sm" />
                                <span className="text-spiders-success text-sm">Trending</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trending Sidebar — only when viewing search results */}
      {isSearching && trending.length > 0 && (
        <div className="w-80 p-4 hidden lg:block">
          <div className="spiders-card sticky top-4">
            <div className="p-4 border-b border-spiders-border">
              <h2 className="text-lg font-semibold text-spiders-text-primary flex items-center gap-2">
                <FaChartLine className="text-spiders-accent-400" />
                Trending
              </h2>
            </div>
            <div>
              {trending.slice(0, 8).map((hashtag, index) => (
                <button
                  key={hashtag._id}
                  onClick={() => handleHashtagClick(hashtag.name || hashtag._id)}
                  className="w-full text-left hover:bg-spiders-bg-elevated/50 p-3 border-b border-spiders-border last:border-b-0 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-spiders-text-muted text-xs w-4 text-right">{index + 1}</span>
                    <div className="flex-1">
                      <p className="text-spiders-text-primary font-medium text-sm">#{hashtag.name || hashtag._id}</p>
                      <p className="text-spiders-text-muted text-xs">{hashtag.usageCount} posts</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;
